<template>
  <v-dialog
    v-model="visible"
    :max-width="timeFields ? 638 : 397"
    persistent
  >
    <v-card v-if="visible">
      <form novalidate @submit.prevent="applyPeriod">
        <v-card-title class="dialog__header">
          <span class="dialog__title">{{ $t(`${namespace}.choosePeriod`) }}</span>
          <v-btn
            class="dialog__close-btn"
            icon
            @click="close"
          >
            <v-icon dark>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog__body">
          <transition name="fade">
            <div v-if="alert.visibility" class="form__alert">
              <v-icon color="error">warning</v-icon> <span>{{ alert.message }}</span>
            </div>
          </transition>

          <div class="period__container">
            <h2 class="period__subtitle">{{ $t(`${namespace}.from`) }}…</h2>
            <v-layout>
              <v-flex :class="timeFields ? 'sm7' : 'sm12'">
                <div class="pr-2--desktop period__calendar__container">
                  <label>{{ $t(`${namespace}.fieldStartDateLabel`) }}*</label>
                  <v-text-field
                    v-model="filters.startDate"
                    :name="$t(`${namespace}.fieldStartDateName`)"
                    :placeholder="$t(`${namespace}.fieldStartDatePlaceholder`)"
                    append-icon="today"
                    color="primary"
                    v-mask="'##/##/####'"
                    return-masked-value
                    v-validate="{ required: true, isValidDate: true }"
                    :error-messages="errors.collect($t(`${namespace}.fieldStartDateName`))"
                    :data-vv-name="$t(`${namespace}.fieldStartDateName`)"
                    required
                    outlined
                    ref="startDate"
                    @click:append="openCalendar('startDateDialog')"
                  />
                  <v-dialog v-model="startDateDialog" width="290" persistent>
                    <v-date-picker
                      v-model="startDate"
                      prev-icon="keyboard_arrow_left"
                      next-icon="keyboard_arrow_right"
                      color="primary"
                      :locale="selectedlocale"
                      :max="today"
                      @change="(val) => closeCalendar('startDateDialog', 'startDate', val)"
                    />
                  </v-dialog>
                </div>
              </v-flex>

              <v-flex v-if="timeFields" sm5>
                <div class="pl-2--desktop">
                  <label>{{ $t(`${namespace}.fieldStartTimeLabel`) }}</label>
                  <v-text-field
                    v-model="startTime"
                    :name="$t(`${namespace}.fieldStartTimeName`)"
                    :placeholder="$t(`${namespace}.fieldStartTimePlaceholder`)"
                    append-icon="query_builder"
                    color="primary"
                    v-mask="'##:##'"
                    return-masked-value
                    v-validate="{ required: Boolean(endTime), date_format: 'HH:mm' }"
                    :error-messages="errors.collect($t(`${namespace}.fieldStartTimeName`))"
                    :data-vv-name="$t(`${namespace}.fieldStartTimeName`)"
                    required
                    outlined
                  />
                </div>
              </v-flex>
            </v-layout>
          </div>

          <div class="period__container">
            <h2 class="period__subtitle">{{ $t(`${namespace}.to`) }}…</h2>
            <v-layout>
              <v-flex :class="timeFields ? 'sm7' : 'sm12'">
                <div class="pr-2--desktop">
                  <label>{{ $t(`${namespace}.fieldEndDateLabel`) }}*</label>
                  <v-text-field
                    v-model="filters.endDate"
                    :name="$t(`${namespace}.fieldEndDateName`)"
                    :placeholder="$t(`${namespace}.fieldEndDatePlaceholder`)"
                    append-icon="today"
                    color="primary"
                    v-mask="'##/##/####'"
                    return-masked-value
                    v-validate="{ required: true, isValidDate: true }"
                    :error-messages="errors.collect($t(`${namespace}.fieldEndDateName`))"
                    :data-vv-name="$t(`${namespace}.fieldEndDateName`)"
                    required
                    outlined
                    ref="endDate"
                    @click:append="openCalendar('endDateDialog')"
                  />
                  <v-dialog v-model="endDateDialog" width="290" persistent>
                    <v-date-picker
                      v-model="endDate"
                      prev-icon="keyboard_arrow_left"
                      next-icon="keyboard_arrow_right"
                      color="primary"
                      :locale="selectedlocale"
                      :min="startDate"
                      :max="today"
                      @change="(val) => closeCalendar('endDateDialog', 'endDate', val)"
                    />
                  </v-dialog>
                </div>
              </v-flex>

              <v-flex v-if="timeFields" sm5>
                <div class="pl-2--desktop">
                  <label>{{ $t(`${namespace}.fieldEndTimeLabel`) }}*</label>
                  <v-text-field
                    v-model="endTime"
                    :name="$t(`${namespace}.fieldEndTimeName`)"
                    :placeholder="$t(`${namespace}.fieldEndTimePlaceholder`)"
                    append-icon="query_builder"
                    color="primary"
                    v-mask="'##:##'"
                    return-masked-value
                    v-validate="{ required: Boolean(startTime), date_format: 'HH:mm' }"
                    :error-messages="errors.collect($t(`${namespace}.fieldEndTimeName`))"
                    :data-vv-name="$t(`${namespace}.fieldEndTimeName`)"
                    required
                    outlined
                  />
                </div>
              </v-flex>
            </v-layout>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="dialog__footer">
          <v-spacer />

          <a class="accent--text mr-4" @click.prevent="close">
            {{ $t('Common.cancel') }}
          </a>

          <v-btn
            type="submit"
            color="primary"
            rounded
            depressed
          >
            {{ $t(`${namespace}.applyPeriod`) }}
          </v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import { mask } from 'vue-the-mask'
import { catchError } from 'corp-front-vue-commons/src/utils/functions'
import moment from 'libs/moment'

export default {
  name: 'Dialog',
  directives: { mask },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    timeFields: {
      type: Boolean,
      default: true
    },
    maxPeriod: {
      type: Number,
      default: 3
    }
  },
  computed: {
    ...mapState({
      selectedlocale: ({ locale }) => locale.symbols.lang
    }),

    today () {
      return new Date().toJSON().slice(0, 10)
    }
  },
  data () {
    return {
      namespace: 'Components.MenuItemPeriod',

      // alert
      alert: {
        visibility: false,
        message: false
      },

      // dialogs
      startDateDialog: false,
      endDateDialog: false,

      // temp props
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
      startDateTemp: null,
      endDateTemp: null,

      filters: {
        startDate: null,
        endDate: null
      }
    }
  },
  methods: {
    resetFields () {
      // alert
      this.alert = {
        visibility: false,
        message: false
      }

      // dialogs
      this.startDateDialog = false
      this.endDateDialog = false

      // temp props
      this.startDate = null
      this.startTime = null
      this.endDate = null
      this.endTime = null
      this.startDateTemp = null
      this.endDateTemp = null

      this.filters = {
        startDate: null,
        endDate: null
      }
    },

    alertError ({ visibility, message }) {
      this.alert = { visibility, message }
    },

    close ($event, val = 'currentMonth') {
      this.$emit('close', val)
    },

    convertDateToEnFormat (str) {
      return str.split('/').reverse().join('-')
    },

    convertDateToPtBrFormat (str) {
      return str.split('-').reverse().join('/')
    },

    openCalendar (dialogProp) {
      this[dialogProp] = true
    },

    closeCalendar (dialogProp, prop, val) {
      this[dialogProp] = false

      if (val) {
        this[prop] = val
        this.filters[prop] = this.convertDateToPtBrFormat(val)
        this.$refs[prop].focus()
      }
    },

    hideAlertError () {
      setTimeout(() => this.alertError({ visibility: false, message: null }), 4000)
    },

    validPeriod () {
      const today = moment().format('YYYY-MM-DD')
      const currentTime = moment().format('HH:mm')

      this.startDateTemp = this.startTime
        ? `${this.startDate}T${this.startTime}`
        : `${this.startDate}T00:00`

      this.endDateTemp = this.endTime
        ? `${this.endDate}T${this.endTime}`
        : `${this.endDate}T23:59`

      if (
        this.startDate === this.endDate &&
        this.startDate === today &&
        !this.startTime && !this.endTime
      ) {
        this.endDateTemp = `${this.endDate}T${currentTime}`
      }

      if (this.endDate === today && !this.endTime) {
        this.endDateTemp = `${this.endDate}T${currentTime}`
      }

      const todayMilliseconds = new Date().getTime()
      const startDateMilliseconds = new Date(this.startDateTemp).getTime()
      const endDateMilliseconds = new Date(this.endDateTemp).getTime()
      const monthsDiff = moment(this.endDateTemp).diff(moment(this.startDateTemp), 'months', true)

      if (endDateMilliseconds > todayMilliseconds) {
        this.alertError({
          visibility: true,
          message: this.$t(`${this.namespace}.endDateLimitError`)
        })

        this.hideAlertError()
        this.$refs.endDate.focus()
        return false
      }

      if (endDateMilliseconds <= startDateMilliseconds) {
        this.alertError({
          visibility: true,
          message: this.$t(`${this.namespace}.periodRangeError`)
        })

        this.hideAlertError()
        this.$refs.endDate.focus()
        return false
      }

      if (monthsDiff > this.maxPeriod) {
        this.alertError({
          visibility: true,
          message: this.$t(`${this.namespace}.periodRangeLimitError`).replace('[VAL]', this.maxPeriod)
        })

        this.hideAlertError()
        return false
      }

      return true
    },

    async applyPeriod () {
      try {
        const isValid = await this.$validator.validateAll()

        if (!isValid) {
          this.alertError({
            visibility: true,
            message: this.$t(`${this.namespace}.fillInAdate`)
          })

          this.hideAlertError()
        }

        if (isValid && this.validPeriod()) {
          const startDate = this.startDateTemp
          const endDate = this.endDateTemp
          this.close(null, 'custom')
          this.$emit('applyPeriod', { startDate, endDate })
        }
      } catch (error) {
        catchError(error)
      }
    }
  },
  watch: {
    visible () {
      this.resetFields()
    },

    'filters.startDate' (val) {
      this.alertError({ visibility: false, message: null })

      if (val && val.length === 10) {
        this.startDate = this.convertDateToEnFormat(val)
      }
    },

    'filters.endDate' (val) {
      this.alertError({ visibility: false, message: null })

      if (val && val.length === 10) {
        this.endDate = this.convertDateToEnFormat(val)
      }
    }
  },
  created () {
    this.$validator.extend('isValidDate', {
      getMessage: field => this.$t(`${this.namespace}.invalidDate`),
      validate: value => {
        if (!value || value.length < 10) return false
        return moment(this.convertDateToEnFormat(value))._isValid
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.period__subtitle {
  font-family: $title-font;
  font-size: $base-font-size;
  font-weight: bold;
  margin-bottom: 5px;
}

.form__alert {
  background-color: $error-2;
  border-bottom: 1px solid $gray-4;
  color: #a94442;
  font-size: $base-font-size;
  line-height: normal;
  margin-bottom: 24px;
  padding: 20px 18px;
  text-align: center;

  .v-icon, span {
    vertical-align: middle;
  }

  .v-icon {
    margin-right: 5px;
  }
}

.v-dialog--custom__content {
  position: relative;
}

.form__alert {
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1000;
}
</style>
